import React from 'react'

/**
 * ListHeightOffsetContext is a React context that allows setting a default height offset for the ListWrapper component.
 * If we set a default per layout, then every usage of ListWrapper doesn't need to set the heightOffset prop itself.
 */

interface ListHeightOffsetContextValue {
  heightOffsets: number[]
  addHeightOffset: (offset: number) => void
  popHeightOffset: () => void
}

const ListHeightOffsetContext = React.createContext<ListHeightOffsetContextValue>({
  heightOffsets: [0],
  addHeightOffset: () => {},
  popHeightOffset: () => {}
})

type ListHeightOffsetContextProviderProps = React.PropsWithChildren<{
  value: number
}>

export const useListHeightOffset = () => {
  const context = React.useContext(ListHeightOffsetContext)
  const total = context.heightOffsets.reduce((a, b) => a + b, 0)
  return total
}

/**
 * ListHeightOffsetContextProvider can be used to define a default height offset for the ListWrapper, which is used as the base offset
 * for every ListWrapper rendered as a child.
 */
export const ListHeightOffsetContextProvider = ({ children, value }: ListHeightOffsetContextProviderProps) => {
  const [heightOffsets, setHeightOffsets] = React.useState([value])
  const addHeightOffset = (value: number) => setHeightOffsets(prev => [...prev, value])
  const popHeightOffset = () => setHeightOffsets(prev => prev.slice(0, -1))

  return (
    <ListHeightOffsetContext.Provider value={{ heightOffsets, addHeightOffset, popHeightOffset }}>
      {children}
    </ListHeightOffsetContext.Provider>
  )
}

/**
 * The useAddListHeightOffset hook can be used to influence the height offset in components that don't directly render the List component.
 *  For example: FormButtons which renders a row of Save/Cancel buttons, which take up vertical space above the ListWrapper.
 */
export const useAddListHeightOffset = (heightOffset: number) => {
  const context = React.useContext(ListHeightOffsetContext)
  React.useEffect(() => {
    context.addHeightOffset(heightOffset)
    return () => {
      context.popHeightOffset() // this works because unmount is always called opposite of mount
    }
  }, [])
}
